/**
 * packages/auth-client/src/utils.js
 * Isomorphic JWT utilities -- no React, no browser APIs required.
 * Safe to use in Node.js server code and browser code.
 */

/**
 * Decodes the payload of a JWT without verifying the signature.
 * Safe for client-side display (email, roles). Never use for server auth decisions.
 * @param {string} token
 * @returns {object|null}
 */
export function decodeJwtPayload(token: string) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json =
      typeof atob !== 'undefined'
        ? atob(b64)
        : Buffer.from(b64, 'base64').toString('utf8');
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Returns true if the JWT exp claim is in the past (or payload is missing).
 * @param {string} token
 * @returns {boolean}
 */
export function isTokenExpired(token: string) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return Date.now() / 1000 >= payload.exp;
}

/**
 * Builds the Authorization header value for a given JWT.
 * @param {string} token
 * @returns {string} "Bearer <token>"
 */
export function getAuthHeader(token: string) {
  return `Bearer ${token}`;
}

// ─── Cross-subdomain cookie storage (browser only) ───────────────────────────
//
// Tokens are stored as a cookie with domain=.core-loop.dev so they are shared
// across all subdomains (core-loop.dev, finance.core-loop.dev, social.core-loop.dev, etc.).
// sessionStorage is per-origin and would require re-login on every subdomain.
//
// Security properties:
//   SameSite=Lax  -- prevents CSRF on navigation; Strict breaks OAuth redirects
//   Secure        -- HTTPS only in production; omitted on localhost
//   No HttpOnly   -- SPA must read the token to decode claims (email, roles)
//   exp claim     -- JWT is already time-limited; cookie max-age mirrors JWT TTL

const TOKEN_COOKIE = 'core-loop.auth.token';
const REFRESH_COOKIE = 'core-loop.auth.refresh';
const TOKEN_MAX_AGE = 15 * 60;          // 15 min

function isSubdomain(): boolean {
  try {
    const h = window.location.hostname;
    return h === 'core-loop.dev' || h.endsWith('.core-loop.dev');
  } catch { return false; }
}

function writeCookie(value: string): void {
  try {
    const domain = isSubdomain() ? '; domain=.core-loop.dev' : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    // eslint-disable-next-line @microsoft/sdl/no-cookies -- auth token; SameSite=Lax; Secure on HTTPS; cross-subdomain sharing required
    document.cookie = `${TOKEN_COOKIE}=${encodeURIComponent(value)}; max-age=${TOKEN_MAX_AGE}; path=/${domain}${secure}; SameSite=Lax`;
  } catch { /* ignore -- SSR / cookie blocked */ }
}

function readCookie(): string | null {
  try {
    // eslint-disable-next-line @microsoft/sdl/no-cookies -- short-lived access token required for cross-subdomain Bearer requests
    const match = document.cookie.split(';')
      .map(c => c.trim())
      .find(c => c.startsWith(`${TOKEN_COOKIE}=`));
    return match ? decodeURIComponent(match.slice(TOKEN_COOKIE.length + 1)) : null;
  } catch { return null; }
}

function deleteCookie(): void {
  try {
    const domain = isSubdomain() ? '; domain=.core-loop.dev' : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    // eslint-disable-next-line @microsoft/sdl/no-cookies -- deleting auth cookie
    document.cookie = `${TOKEN_COOKIE}=; max-age=0; path=/${domain}${secure}; SameSite=Lax`;
  } catch { /* ignore */ }
}

export function getStoredToken(): string | null {
  // Prefer cookie (cross-subdomain); fall back to sessionStorage for migration
  const cookie = readCookie();
  if (cookie) return cookie;
  try {
    const ss = sessionStorage.getItem(TOKEN_COOKIE);
    if (ss) {
      // Migrate: move legacy sessionStorage token into cookie so next load works cross-subdomain
      writeCookie(ss);
      sessionStorage.removeItem(TOKEN_COOKIE);
      return ss;
    }
  } catch { /* ignore */ }
  return null;
}

export function setStoredToken(token: string): void {
  writeCookie(token);
  // Clear any lingering sessionStorage so there's no stale fallback
  try { sessionStorage.removeItem(TOKEN_COOKIE); } catch { /* ignore */ }
}

export function clearStoredToken(): void {
  deleteCookie();
  try { sessionStorage.removeItem(TOKEN_COOKIE); } catch { /* ignore */ }
}

export function clearStoredRefreshToken(): void {
  try {
    const domain = isSubdomain() ? '; domain=.core-loop.dev' : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    // eslint-disable-next-line @microsoft/sdl/no-cookies -- deleting refresh token cookie
    document.cookie = `${REFRESH_COOKIE}=; max-age=0; path=/${domain}${secure}; SameSite=Lax`;
  } catch { /* ignore */ }
}

